import { useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { FeatureFlag } from '../../constants/featureFlags';
import { useInbox, useUnreadTotal } from '../../hooks/chat/useChatQueries';
import { useChatEvents } from '../../hooks/chat/useChatEvents';
import { useOpenChatFromChart } from '../../hooks/chat/useOpenChatFromChart';
import { useUnreadTitle } from '../../hooks/chat/useUnreadTitle';
import { ChatPanel } from './ChatPanel';
import { ChatGlyph } from './ChatGlyph';

/**
 * The one chat mount point, in Layout so the panel survives route changes.
 * Gated by the WorkOS `chat` flag alone — there is no access slug (EMR-1881).
 *
 * The gate is a separate component from the hooks on purpose: every hook below
 * uses TanStack Query, and Layout.test.tsx renders `<Layout />` with no
 * QueryClientProvider. Running one of them for a flag-off user would throw
 * there, and would also fire a 403 GET /chat/inbox for that user.
 */
export function ChatLauncher() {
  const enabled = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );
  if (!enabled) return null;
  return <ChatLauncherInner />;
}

function ChatLauncherInner() {
  const isChatOpen = useStore(state => state.isChatOpen);
  const openFromChart = useOpenChatFromChart();
  const closeChat = useStore(state => state.closeChat);
  useInbox();
  const unread = useUnreadTotal();
  useChatEvents();
  useUnreadTitle(unread);

  // Keyboard map: ⌘. (mac) / Ctrl. (others) toggles chat from anywhere. It
  // lives here, not in ChatPanel, because the panel is unmounted while closed.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '.' || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      e.preventDefault();
      if (useStore.getState().isChatOpen) closeChat();
      else openFromChart();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openFromChart, closeChat]);

  // The launcher is a fresh button after the panel closes, so focus must be
  // put back on it by hand.
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !isChatOpen) launcherRef.current?.focus();
    wasOpen.current = isChatOpen;
  }, [isChatOpen]);

  return (
    <>
      {/* V7 solid button from the "Chat icon redesign" design project: a light
          purple tint disc with the deep purple glyph. The tint and glyph
          shades are design values, not theme tokens, so they are written out. */}
      {!isChatOpen && (
        <button
          ref={launcherRef}
          type='button'
          aria-label='Messages'
          onClick={openFromChart}
          className='fixed bottom-4 right-4 z-40 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[hsl(252_78%_96%)] text-[hsl(252_78%_56%)] hover:bg-[hsl(252_78%_92%)]'
        >
          <ChatGlyph className='h-7 w-7' />
          {unread > 0 && (
            <span className='absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-medium text-white ring-2 ring-white'>
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      )}
      <ChatPanel />
    </>
  );
}
